import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  DollarSign, 
  Calendar,
  Building2,
  User,
  FileText,
  AlertCircle,
  Info,
  CreditCard,
  Shield,
  ArrowRight,
  Download,
  Mail,
  Phone,
  Zap,
  Timer
} from 'lucide-react';
import api from '../../utils/api';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51S13rxBnBT7CggSVggNOdJmGCBnUmZOdV3z5psTElNtVIBIJC1kJWsjbBwDxUG35yhjwAeR7bJk431J578QNABRQ008FawJFLT');

const GuestInvoicePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { openPreferences } = useCookieConsent();
  
  const [invoice, setInvoice] = useState(null);
  const [guestInvoice, setGuestInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null); // 'immediate' or 'due_date'
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'processing', 'succeeded', 'failed'
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [showRetryForm, setShowRetryForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);
  const [isApproving, setIsApproving] = useState(false);

  const seo = (
    <Helmet>
      <title>Guest Invoice | Receevit®</title>
      <meta name="description" content="Securely view and pay your Receevit® invoice. Approve, pay now, or pay on the due date with protected payment processing." />
      <link rel="canonical" href="https://www.receevit.com/invoice" />
      <meta property="og:title" content="Guest Invoice | Receevit®" />
      <meta property="og:description" content="Securely view and pay your Receevit® invoice. Approve, pay now, or pay on the due date with protected payment processing." />
      <meta property="og:url" content={`https://www.receevit.com/invoice${token ? `/${token}` : ''}`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://www.receevit.com/og-image.png" />
    </Helmet>
  );

  // Fetch invoice data on component mount
  useEffect(() => {
    console.log('🔍 GuestInvoicePage mounted with token:', token);
    console.log('🔍 Token type:', typeof token);
    console.log('🔍 Token length:', token ? token.length : 'undefined');
    fetchInvoiceData();
  }, [token]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching invoice data for token:', token);
      console.log('🔍 API URL:', `/api/guest-invoice/${token}`);
      
      const response = await api.get(`/api/guest-invoice/${token}`);
      
      console.log('🔍 API Response:', response.data);
      
      if (response.data.success) {
        setInvoice(response.data.data.invoice);
        setGuestInvoice(response.data.data.guestInvoice);
        setError(null);
        console.log('✅ Invoice data loaded successfully');
      } else {
        console.error('❌ API returned success: false', response.data.message);
        setError(response.data.message || 'Failed to fetch invoice');
      }
    } catch (error) {
      console.error('❌ Error fetching invoice:', error);
      console.error('❌ Error response:', error.response?.data);
      if (error.response?.status === 404) {
        setError('Invoice not found or access token invalid');
      } else if (error.response?.status === 410) {
        setError('Invoice access has expired. Please contact the sender for a new link.');
      } else {
        setError('Failed to load invoice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentOptionSelect = async (option) => {
    // If selecting "due_date" (approve), immediately disable the button
    if (option === 'due_date') {
      setIsApproving(true);
    }
    
    setSelectedPaymentOption(option);
    setActionLoading(true);
    
    try {
      // Create PaymentIntent (immediate) or SetupIntent (due_date)
      const intentResponse = await api.post(`/api/guest-invoice/${token}/create-payment-intent`, {
        paymentOption: option
      });
      
      if (intentResponse.data.success) {
        setClientSecret(intentResponse.data.clientSecret);
        
        if (option === 'immediate') {
          setPaymentIntentId(intentResponse.data.paymentIntentId);
        } else {
          // For due_date, store setupIntentId
          setPaymentIntentId(intentResponse.data.setupIntentId);
        }
        
        setShowPaymentForm(true);
      } else {
        console.error('Failed to create intent:', intentResponse.data);
        alert('Failed to create payment intent: ' + (intentResponse.data.error || 'Unknown error'));
        
        // On error, check if approval actually succeeded in Stripe before re-enabling
        if (option === 'due_date') {
          await checkApprovalStatus();
        }
      }
    } catch (error) {
      console.error('Error creating intent:', error);
      alert(error.response?.data?.error || 'Failed to create payment intent. Please try again.');
      
      // On error, check if approval actually succeeded in Stripe before re-enabling
      if (option === 'due_date') {
        await checkApprovalStatus();
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Check if invoice was actually approved in Stripe
  const checkApprovalStatus = async () => {
    try {
      const response = await api.get(`/api/guest-invoice/${token}`);
      if (response.data.success) {
        const invoice = response.data.data.invoice;
        const guestInvoice = response.data.data.guestInvoice;
        
        // If invoice is approved or paid, keep button disabled
        if (invoice?.status === 'approved' || invoice?.status === 'paid' || 
            guestInvoice?.status === 'approved' || guestInvoice?.status === 'paid') {
          console.log('✅ Approval succeeded despite error response');
          // Update local state
          setInvoice(invoice);
          setGuestInvoice(guestInvoice);
          // Keep button disabled
          return;
        }
      }
    } catch (checkError) {
      console.error('Failed to verify invoice status:', checkError);
    }
    
    // Approval truly failed, re-enable button
    setIsApproving(false);
  };



  const handleRetryPayment = async (paymentMethodId) => {
    try {
      setActionLoading(true);
      setPaymentStatus('processing');
      
      const retryResponse = await api.post(`/api/guest-invoice/${token}/retry-payment`, {
          paymentMethodId: paymentMethodId
        });
        
      if (retryResponse.data.success) {
          // Update local state
          setGuestInvoice(prev => ({ ...prev, status: 'paid', approvalDate: new Date() }));
          setInvoice(prev => ({ ...prev, status: 'paid' }));
        setShowRetryForm(false);
        setPaymentStatus('succeeded');
        setRetryAttempts(retryResponse.data.retryAttempts || 0);
          
          // Show success message
        alert('Payment retry successful!');
        } else {
        setPaymentStatus('failed');
        setRetryAttempts(retryResponse.data.retryAttempts || 0);
        alert('Payment retry failed: ' + retryResponse.data.message);
      }
    } catch (error) {
      console.error('Error retrying payment:', error);
      setPaymentStatus('failed');
      alert(error.response?.data?.message || 'Failed to retry payment. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    // Validate rejection reason is provided
    if (!rejectionReason || rejectionReason.trim().length === 0) {
      alert('Please provide a reason for rejecting this invoice.');
      return;
    }
    
    if (rejectionReason.trim().length < 10) {
      alert('Rejection reason must be at least 10 characters.');
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await api.post(`/api/guest-invoice/${token}/reject`, {
        reason: rejectionReason.trim()
      });
      
      if (response.data.success) {
        // Update local state
        setGuestInvoice(prev => ({ 
          ...prev,
          status: 'rejected', 
          approvalDate: new Date(),
          rejectionReason: rejectionReason || 'No reason provided'
        }));
        setInvoice(prev => ({ ...prev, status: 'rejected' }));
        setShowRejectionForm(false);
        
        // Show success message
        alert('Invoice rejected successfully. The sender has been notified.');
      }
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      alert(error.response?.data?.error || 'Failed to reject invoice. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending Approval' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' },
      expired: { color: 'bg-gray-100 text-gray-800', icon: Clock, text: 'Expired' },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Paid' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-2" />
        {config.text}
      </span>
    );
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    // Convert to number (database returns DECIMAL as string)
    const amountInPounds = parseFloat(amount || 0);
    if (amountInPounds < 0) {
      return `(£${Math.abs(amountInPounds).toFixed(2)})`;
    }
    return `£${amountInPounds.toFixed(2)}`;
  };

  // Stripe Elements Payment Form Component
  const PaymentForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event) => {
      event.preventDefault();
      
      if (!stripe || !elements || !clientSecret) {
        return;
      }

      setProcessing(true);

      try {
        if (selectedPaymentOption === 'immediate') {
          // IMMEDIATE PAYMENT: Confirm PaymentIntent
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: window.location.href,
            },
            redirect: 'if_required'
          });

          if (error) {
            if (error.type === 'card_error' || error.type === 'validation_error') {
              alert(`Payment failed: ${error.message}`);
            } else {
              alert('An unexpected error occurred. Please try again.');
            }
            setProcessing(false);
            return;
          }

          // Payment successful - update invoice status via API
          if (paymentIntent.status === 'succeeded') {
            try {
              // Call backend to update invoice status
              await api.post(`/api/guest-invoice/${token}/mark-paid`, {
                paymentIntentId: paymentIntent.id
              });
              
              setGuestInvoice(prev => ({ ...prev, status: 'paid' }));
              setInvoice(prev => ({ ...prev, status: 'paid' }));
              setShowPaymentForm(false);
              setPaymentStatus('succeeded');
              
              const details = {
                type: 'immediate',
                amount: invoice.total,
                invoiceNumber: invoice.invoiceNumber,
                paymentIntentId: paymentIntent.id
              };
              
              console.log('🎉 Payment successful! Showing success modal:', details);
              setSuccessDetails(details);
              
              // Use setTimeout to ensure state updates properly
              setTimeout(() => {
                setShowSuccessModal(true);
              }, 100);
            } catch (err) {
              console.error('Error updating invoice status:', err);
              alert('Payment succeeded but failed to update invoice status. Please contact support.');
              setProcessing(false);
            }
          } else {
            alert(`Payment status: ${paymentIntent.status}. Please contact support.`);
            setProcessing(false);
          }
        } else {
          // PAY ON DUE DATE: Confirm SetupIntent and save payment method
          const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            confirmParams: {
              return_url: window.location.href,
            },
            redirect: 'if_required'
          });

          if (error) {
            // Check if approval actually succeeded despite error
            await checkApprovalStatus();
            
            if (error.type === 'card_error' || error.type === 'validation_error') {
              alert(`Setup failed: ${error.message}`);
            } else {
              alert('An unexpected error occurred. Please try again.');
            }
            setProcessing(false);
            return;
          }

          // Setup successful - save payment method
          if (setupIntent.status === 'succeeded') {
            const confirmResponse = await api.post(`/api/guest-invoice/${token}/confirm-setup`, {
              setupIntentId: setupIntent.id,
              paymentMethodId: setupIntent.payment_method
            });

            if (confirmResponse.data.success) {
              setGuestInvoice(prev => ({ ...prev, status: 'approved' }));
              setInvoice(prev => ({ ...prev, status: 'approved' }));
              setShowPaymentForm(false);
              setPaymentStatus('succeeded');
              setIsApproving(false); // Approval succeeded
              setSuccessDetails({
                type: 'scheduled',
                amount: invoice.total,
                invoiceNumber: invoice.invoiceNumber,
                dueDate: invoice.dueDate,
                setupIntentId: setupIntent.id
              });
              setShowSuccessModal(true);
            } else {
              // Check if approval actually succeeded despite error
              await checkApprovalStatus();
              alert('Failed to save payment method. Please try again.');
              setProcessing(false);
            }
          } else {
            // Check if approval actually succeeded despite error
            await checkApprovalStatus();
            alert(`Setup status: ${setupIntent.status}. Please contact support.`);
            setProcessing(false);
          }
        }
      } catch (err) {
        console.error('Payment error:', err);
        
        // If this was an approval attempt (due_date), check if it actually succeeded
        if (selectedPaymentOption === 'due_date') {
          await checkApprovalStatus();
        }
        
        alert(err.response?.data?.error || 'Payment failed. Please try again.');
        setProcessing(false);
      }
    };

    const approveInvoice = async () => {
      try {
        const approveResponse = await api.post(`/api/guest-invoice/${token}/approve`, {
          paymentIntentId: paymentIntentId
        });
        
        if (approveResponse.data.success) {
          // Update local state
          const newStatus = approveResponse.data.data.status;
          setGuestInvoice(prev => ({ ...prev, status: newStatus, approvalDate: new Date() }));
          setInvoice(prev => ({ ...prev, status: newStatus }));
          setShowPaymentForm(false);
          setPaymentStatus('succeeded');
          
          // Show success message
          if (newStatus === 'paid') {
            alert('Payment completed successfully!');
          } else {
            alert('Invoice approved! Payment will be processed on the due date.');
          }
        } else {
          setPaymentStatus('failed');
          alert('Invoice approval failed: ' + approveResponse.data.message);
        }
      } catch (error) {
        console.error('Error approving invoice:', error);
        setPaymentStatus('failed');
        alert('Invoice approval failed. Please try again.');
      } finally {
        setProcessing(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            {selectedPaymentOption === 'immediate' ? 'Immediate Payment' : 'Payment Method Setup'}
          </h3>
          <p className="text-sm text-blue-800">
            {selectedPaymentOption === 'immediate' 
              ? `Enter your card details to pay ${formatCurrency(invoice?.total)} now.`
              : `Enter your card details to set up payment for ${formatCurrency(invoice?.total)} on the due date.`
            }
          </p>
        </div>

        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Details
          </label>
          {clientSecret ? (
            <div>
              <div id="payment-element-container" className="min-h-[200px]">
                <PaymentElement
                  options={{
                    layout: 'tabs',
                    paymentMethodOrder: ['card'],
                    fields: {
                      billingDetails: 'auto'
                    }
                  }}
                  onLoadError={(error) => {
                    console.error('PaymentElement load error:', error);
                  }}
                  onReady={() => {
                    // Payment element loaded successfully
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading payment form...</p>
            </div>
          )}
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={!stripe || processing || actionLoading}
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing || actionLoading ? 'Processing...' : 
              selectedPaymentOption === 'immediate' 
                ? `Pay ${formatCurrency(invoice?.total)} Now`
                : `Set Up Payment for ${formatCurrency(invoice?.total)}`
            }
          </button>
          <button
            type="button"
            onClick={() => {
              setShowPaymentForm(false);
              setSelectedPaymentOption(null);
              // If cancelling approval, re-enable button (but check status first)
              if (selectedPaymentOption === 'due_date') {
                checkApprovalStatus();
              }
            }}
            className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  // Retry Payment Form Component
  const RetryPaymentForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event) => {
      event.preventDefault();
      
      if (!stripe || !elements) {
        return;
      }

      setProcessing(true);

      try {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(PaymentElement),
        });

        if (error) {
          alert(`Payment method creation failed: ${error.message}`);
          setProcessing(false);
          return;
        }

        await handleRetryPayment(paymentMethod.id);
      } catch (err) {
        console.error('Retry payment error:', err);
        alert('Payment retry failed. Please try again.');
      } finally {
        setProcessing(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-orange-900 mb-2">Retry Payment</h3>
          <p className="text-sm text-orange-800">
            Previous payment attempt failed. Please try again with your card details.
            {retryAttempts > 0 && ` (Attempt ${retryAttempts + 1})`}
          </p>
        </div>

        <div className="bg-white border border-gray-300 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Details
          </label>
          <PaymentElement
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card'],
              fields: {
                billingDetails: 'auto'
              }
            }}
          />
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={!stripe || processing || actionLoading}
            className="flex-1 bg-orange-600 text-white py-3 px-4 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing || actionLoading ? 'Retrying...' : `Retry Payment - ${formatCurrency(invoice?.total)}`}
          </button>
          <button
            type="button"
            onClick={() => setShowRetryForm(false)}
            className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Extract invoice HTML generation into a reusable function (used by both print and PDF)
  const generateInvoiceHTML = () => {
    const formatCurrency = (amount) => {
      const numAmount = parseFloat(amount || 0);
      return `£${numAmount.toFixed(2)}`;
    };
    
    const guestRecipient = typeof invoice.guestRecipient === 'string' 
      ? JSON.parse(invoice.guestRecipient) 
      : invoice.guestRecipient;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoiceNumber || 'Invoice'}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            body { 
              font-family: 'Inter', 'Segoe UI', system-ui, sans-serif; 
              margin: 40px; 
              color: #1f2937;
              background: #ffffff;
              line-height: 1.6;
            }
            
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              border-radius: 12px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              overflow: hidden;
            }
            
            .invoice-header {
              text-align: center;
              padding: 30px 20px;
              border-bottom: 1px solid #e5e7eb;
              background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            }
            
            .invoice-title {
              font-size: 28px;
              font-weight: 700;
              color: #1f2937;
              margin: 0 0 10px 0;
              letter-spacing: -0.025em;
            }
            
            .company-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              padding: 30px;
            }
            
            .company-card {
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
            }
            
            .company-card h3 {
              font-size: 12px;
              font-weight: 500;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              margin: 0 0 12px 0;
            }
            
            .company-card p {
              margin: 4px 0;
              font-size: 14px;
              color: #374151;
            }
            
            .company-card p:first-of-type {
              font-weight: 600;
              color: #111827;
              font-size: 15px;
            }
            
            .line-items-section {
              padding: 0 30px 30px 30px;
            }
            
            .line-items-section h3 {
              font-size: 12px;
              font-weight: 500;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              margin: 0 0 15px 0;
            }
            
            .line-items-table {
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              overflow: hidden;
            }
            
            .line-items-table table {
              width: 100%;
              border-collapse: collapse;
            }
            
            .line-items-table th {
              background: #f9fafb;
              border-bottom: 1px solid #e5e7eb;
              padding: 12px 16px;
              text-align: left;
              font-size: 11px;
              font-weight: 500;
              color: #6b7280;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .line-items-table td {
              padding: 12px 16px;
              border-bottom: 1px solid #f3f4f6;
              font-size: 14px;
              color: #374151;
            }
            
            .line-items-table tr:last-child td {
              border-bottom: none;
            }
            
            .line-items-table .amount {
              text-align: right;
              font-weight: 500;
            }
            
            .totals-section {
              padding: 0 30px 30px 30px;
              display: flex;
              justify-content: flex-end;
            }
            
            .totals-card {
              width: 300px;
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 8px 0;
              font-size: 14px;
            }
            
            .total-row.grand-total {
              border-top: 1px solid #e5e7eb;
              padding-top: 15px;
              margin-top: 10px;
              font-size: 16px;
              font-weight: 600;
              color: #111827;
            }
            
            .footer {
              text-align: center;
              padding: 30px;
              background: #f9fafb;
              border-top: 1px solid #e5e7eb;
              color: #6b7280;
              font-size: 14px;
            }
            
            @page {
              margin: 0.5in !important;
              size: A4 !important;
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              body { 
                margin: 0 !important; 
                padding: 0 !important;
                font-size: 12px;
                background: white !important;
              }
              html {
                background: white !important;
              }
              .invoice-container { 
                max-width: none !important;
                box-shadow: none !important;
                border-radius: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              .invoice-header { 
                background: white !important;
                border-bottom: 1px solid #e2e8f0;
              }
              .company-section { 
                page-break-inside: avoid;
              }
              .line-items-table { 
                page-break-inside: avoid;
              }
              .totals-section { 
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="invoice-header">
              <h1 class="invoice-title">INVOICE ${invoice.invoiceNumber}</h1>
              <p style="margin-top: 10px; color: #6b7280;">
                <strong>Date:</strong> ${new Date(invoice.createdAt).toLocaleDateString('en-GB')} | 
                <strong>Due Date:</strong> ${new Date(invoice.dueDate).toLocaleDateString('en-GB')} | 
                <strong>Status:</strong> ${invoice.status.toUpperCase()}
              </p>
            </div>
            
            <div class="company-section">
              <div class="company-card">
                <h3>From</h3>
                <p><strong>${invoice.sender?.companyName || (invoice.sender?.firstName || '') + ' ' + (invoice.sender?.lastName || '')}</strong></p>
                <p>${invoice.sender?.email || '[Email]'}</p>
                ${invoice.sender?.address ? `<p style="margin-top: 8px; color: #6b7280; font-size: 13px;">
                  ${invoice.sender.address}<br/>
                  ${invoice.sender.city ? invoice.sender.city + ', ' : ''}${invoice.sender.state || ''} ${invoice.sender.zipCode || ''}<br/>
                  ${invoice.sender.country || ''}
                </p>` : ''}
              </div>
              
              <div class="company-card">
                <h3>To</h3>
                <p><strong>${guestRecipient?.companyName || (guestRecipient?.firstName || '') + ' ' + (guestRecipient?.lastName || '')}</strong></p>
                <p>${guestRecipient?.email || '[Email]'}</p>
                ${guestRecipient?.address ? `<p style="margin-top: 8px; color: #6b7280; font-size: 13px;">
                  ${guestRecipient.address}<br/>
                  ${guestRecipient.city ? guestRecipient.city + ', ' : ''}${guestRecipient.zipCode || ''}<br/>
                  ${guestRecipient.country || ''}
                </p>` : ''}
              </div>
            </div>
            
            <div class="line-items-section">
              <h3>Line Items</h3>
              <div class="line-items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th style="text-align: center;">Qty</th>
                      <th style="text-align: right;">Unit Price</th>
                      <th style="text-align: right;">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${(invoice.items || []).map(item => {
                      const qty = parseFloat(item.quantity || 0);
                      const price = parseFloat(item.unitPrice || 0);
                      const taxRate = parseFloat(item.taxRate || 0) / 100;
                      const discount = parseFloat(item.discount || 0) / 100;
                      const itemTotal = qty * price * (1 + taxRate) * (1 - discount);
                      
                      return `
                      <tr>
                        <td>${item.description || 'Item'}</td>
                        <td style="text-align: center;">${item.quantity || 1}</td>
                        <td class="amount">${formatCurrency(item.unitPrice)}</td>
                        <td class="amount">${formatCurrency(itemTotal)}</td>
                      </tr>
                    `}).join('')}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div class="totals-section">
              <div class="totals-card">
                <div class="total-row">
                  <span>Subtotal:</span>
                  <span>${formatCurrency(invoice.subtotal)}</span>
                </div>
                <div class="total-row">
                  <span>Tax:</span>
                  <span>${formatCurrency(invoice.tax)}</span>
                </div>
                <div class="total-row grand-total">
                  <span>Total:</span>
                  <span>${formatCurrency(invoice.total)}</span>
                </div>
              </div>
            </div>
            
            <div class="footer">
              <p>Powered by Receevit®</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      alert('Please allow popups to print the invoice. You can save as PDF from the print dialog.');
      return;
    }
    
    // Use the shared HTML generation function
    const printContent = generateInvoiceHTML();
    
    // Write content to the new window
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        // Don't close immediately - let user see the print dialog
        // The window will close after print dialog is dismissed
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.close();
          }
        }, 500);
      }, 250);
    };
    
    // Fallback if onload doesn't fire
    setTimeout(() => {
      if (printWindow.document.readyState === 'complete') {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          if (!printWindow.closed) {
            printWindow.close();
          }
        }, 500);
      }
    }, 1000);
  };

  if (loading) {
    return (
      <>
        {seo}
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading invoice...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {seo}
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center p-8">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Error</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  console.log('🔍 Render check - invoice:', invoice ? 'exists' : 'null', 'guestInvoice:', guestInvoice ? 'exists' : 'null');
  
  if (!invoice) {
    console.log('❌ Rendering "not found" because invoice is:', invoice);
    return (
      <>
        {seo}
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">Invoice not found</p>
            <p className="text-xs text-gray-400 mt-2">Debug: invoice={String(!!invoice)}, guestInvoice={String(!!guestInvoice)}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F11ED]">
      {seo}

      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#1F11ED] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <span className="text-[#1F11ED] font-bold text-xl">R</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Receevit®</h1>
                <p className="text-sm text-blue-100">Invoice Approval Portal</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <p className="text-sm text-blue-100">Guest Access</p>
                <p className="text-xs text-blue-200">{guestInvoice?.recipientEmail || invoice.guestRecipient?.email || 'Guest User'}</p>
              </div>
              <button
                onClick={() => window.open('https://www.receevit.com', '_blank')}
                className="bg-white text-[#1F11ED] px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
              >
                Visit Receevit® Home
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-24">
        {/* Invoice Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Invoice {invoice.invoiceNumber}
              </h1>
              <p className="text-gray-600">
                Sent by {invoice.sender?.firstName} {invoice.sender?.lastName}
              </p>
            </div>
            <div className="text-right">
              {getStatusBadge(guestInvoice?.status || invoice.status)}
              <p className="text-sm text-gray-500 mt-2">
                Access Count: {guestInvoice?.accessCount || 1}
              </p>
              <div className="flex space-x-3 mt-3">
                <button
                  onClick={handlePrint}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-sm inline-flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Print Invoice
                </button>
              </div>
            </div>
          </div>

          {/* Key Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(invoice.total)}
                </p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Due Date</p>
                  <p className="text-lg font-semibold text-green-600">
                    {formatDate(invoice.dueDate)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <Clock className="w-6 h-6 text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <p className="text-lg font-semibold text-purple-600">
                    {invoice?.status?.toUpperCase() || 'PENDING'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                From
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">
                  {invoice.sender?.firstName} {invoice.sender?.lastName}
                </p>
                <p className="text-gray-600">{invoice.sender?.email}</p>
                {invoice.sender?.companyName && (
                  <p className="text-gray-600">{invoice.sender.companyName}</p>
                )}
                {invoice.sender?.address && (
                  <p className="text-gray-600 text-sm mt-2">
                    {invoice.sender.address}
                    {invoice.sender.city && `, ${invoice.sender.city}`}
                    {invoice.sender.state && `, ${invoice.sender.state}`}
                    {invoice.sender.zipCode && ` ${invoice.sender.zipCode}`}
                    {invoice.sender.country && `, ${invoice.sender.country}`}
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-blue-600" />
                To
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">
                  {invoice.guestRecipient?.firstName} {invoice.guestRecipient?.lastName}
                </p>
                <p className="text-gray-600">{invoice.guestRecipient?.email}</p>
                {invoice.guestRecipient?.companyName && (
                  <p className="text-gray-600">{invoice.guestRecipient.companyName}</p>
                )}
                {invoice.guestRecipient?.address && (
                  <p className="text-gray-600 text-sm">
                    {invoice.guestRecipient.address}, {invoice.guestRecipient.city}, {invoice.guestRecipient.zipCode}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Invoice Items
            </h3>
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoice.items?.map((item, index) => {
                    // Calculate item total: quantity × unitPrice × (1 + tax) × (1 - discount)
                    const qty = parseFloat(item.quantity || 0);
                    const price = parseFloat(item.unitPrice || 0);
                    const taxRate = parseFloat(item.taxRate || 0) / 100;
                    const discount = parseFloat(item.discount || 0) / 100;
                    const itemTotal = qty * price * (1 + taxRate) * (1 - discount);
                    
                    return (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.description || 'Item'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(itemTotal)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-100">
                  <tr>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      Total:
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-gray-900">
                      {formatCurrency(invoice.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes and Terms */}
          {(invoice.notes || invoice.terms) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {invoice.notes && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
                  <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{invoice.notes}</p>
                </div>
              )}
              {invoice.terms && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Terms</h3>
                  <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{invoice.terms}</p>
                </div>
              )}
            </div>
          )}

          {/* Attachments - Only show if attachments exist */}
          {invoice.attachments && invoice.attachments.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                📎 Attachments ({invoice.attachments.length})
              </h3>
              <div className="space-y-3">
                {invoice.attachments.map((attachment, index) => {
                  const handleDownloadAttachment = async () => {
                    try {
                      const response = await api.get(`/files/download/${encodeURIComponent(attachment.s3Key)}?filename=${encodeURIComponent(attachment.name)}`);
                      if (response.data.success && response.data.downloadUrl) {
                        // Create a temporary link to trigger download with original filename
                        const link = document.createElement('a');
                        link.href = response.data.downloadUrl;
                        link.download = attachment.name; // Use original filename
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      } else {
                        alert('Failed to download file. Please try again.');
                      }
                    } catch (error) {
                      console.error('Error downloading attachment:', error);
                      alert('Error downloading file. Please try again.');
                    }
                  };
                  
                  const formatFileSize = (bytes) => {
                    if (bytes < 1024) return bytes + ' B';
                    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
                  };
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-6 h-6 text-blue-600" />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{attachment.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={handleDownloadAttachment}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium flex items-center space-x-2"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-3 flex items-center">
                <Info className="w-4 h-4 mr-1" />
                Download supporting documents directly from secure storage
              </p>
            </div>
          )}
        </div>

        {/* Retry Payment Section for Failed Payments */}
        {paymentStatus === 'failed' && guestInvoice?.status !== 'paid' && invoice.status !== 'paid' && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl shadow-lg p-8 mb-8">
            <div className="text-center mb-6">
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-orange-900 mb-2">Payment Failed</h2>
              <p className="text-orange-700">
                Your payment attempt was unsuccessful. Please try again with a different payment method.
                {retryAttempts > 0 && ` (Attempt ${retryAttempts + 1})`}
              </p>
            </div>
            
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowRetryForm(true)}
                disabled={actionLoading}
                className="bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Retry Payment'}
              </button>
              <button
                onClick={() => setPaymentStatus(null)}
                className="bg-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

          {/* Early Payment Incentive */}
          {invoice?.earlyPaymentDiscounts && invoice.earlyPaymentDiscounts.length > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    🎉 Early Payment Incentive Available!
                  </h3>
                  <p className="text-blue-800 mb-4">
                    Pay within <strong>{invoice.earlyPaymentDiscounts[0].timeframeDays} days</strong> to earn a discount 
                    of <strong>{invoice.earlyPaymentDiscounts[0].discountAmount ? 
                      `£${invoice.earlyPaymentDiscounts[0].discountAmount}` : 
                      `${invoice.earlyPaymentDiscounts[0].discountPercentage}%`}</strong> 
                    on your next invoice from {invoice.sender?.companyName || 'this sender'}.
                  </p>
                  
                  <div className="bg-blue-100 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-700 font-medium mb-2">
                      💡 <strong>How to claim your discount:</strong>
                    </p>
                    <ol className="text-sm text-blue-600 space-y-1 ml-4">
                      <li>1. Pay this invoice within {invoice.earlyPaymentDiscounts[0].timeframeDays} days</li>
                      <li>2. Create a free Receevit® account with this email address</li>
                      <li>3. Your discount will be automatically applied to your next invoice!</li>
                    </ol>
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <button 
                        onClick={() => navigate('/signup')}
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        Sign Up for Free
                      </button>
                      <button 
                        onClick={() => navigate('/login')}
                        className="bg-gray-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-gray-700 transition-colors"
                      >
                        Already have an account?
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-blue-200 rounded-full h-3 mb-2">
                    <div 
                      className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.max(0, Math.min(100, 
                          ((invoice.earlyPaymentDiscounts[0].timeframeDays - 
                           Math.floor((new Date() - new Date(invoice.createdAt)) / (1000 * 60 * 60 * 24))) / 
                           invoice.earlyPaymentDiscounts[0].timeframeDays) * 100
                        ))}%` 
                      }}
                    ></div>
                  </div>
                  <p className="text-sm text-blue-600 font-medium">
                    {Math.max(0, invoice.earlyPaymentDiscounts[0].timeframeDays - 
                      Math.floor((new Date() - new Date(invoice.createdAt)) / (1000 * 60 * 60 * 24)))} days remaining
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
        {(invoice?.status === 'pending' || invoice?.status === 'pending_approval' || invoice?.status === 'approved') && (
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              How would you like to pay this invoice?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Immediate Payment Button */}
              <button
                onClick={() => handlePaymentOptionSelect('immediate')}
                disabled={actionLoading}
                className="bg-green-600 text-white p-6 rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <div className="text-center">
                  <Zap className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-xl font-bold mb-2">Pay Now</h3>
                  <p className="text-green-100 text-sm">
                    Pay {formatCurrency(invoice?.total)} immediately and clear this invoice.
                  </p>
                </div>
              </button>

              {/* Due Date Payment Button */}
              <button
                onClick={() => handlePaymentOptionSelect('due_date')}
                disabled={actionLoading || isApproving}
                className={`p-6 rounded-xl transition-colors ${
                  isApproving
                    ? 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:opacity-50`}
                title={isApproving ? 'Processing approval...' : ''}
              >
                <div className="text-center">
                  <Timer className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-xl font-bold mb-2">
                    {isApproving ? 'Processing...' : 'Pay on Due Date'}
                  </h3>
                  <p className={`text-sm ${isApproving ? 'text-gray-200' : 'text-blue-100'}`}>
                    {isApproving 
                      ? 'Setting up payment method...' 
                      : `Set up payment to be processed on ${formatDate(invoice?.dueDate)}.`
                    }
                  </p>
                </div>
              </button>

              {/* Reject Button */}
              <button
                onClick={() => setShowRejectionForm(true)}
                disabled={actionLoading}
                className="bg-red-600 text-white p-6 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                <div className="text-center">
                  <XCircle className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-xl font-bold mb-2">Reject Invoice</h3>
                  <p className="text-red-100 text-sm">
                    Reject this invoice and provide a reason for rejection.
                  </p>
                </div>
              </button>
            </div>

            {/* Payment Option Info */}
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Payment Options Explained</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Zap className="w-6 h-6 text-green-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Pay Now</h4>
                    <p className="text-sm text-gray-600">
                      Complete payment immediately and mark the invoice as paid. You'll receive instant confirmation.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Timer className="w-6 h-6 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Pay on Due Date</h4>
                    <p className="text-sm text-gray-600">
                      Set up your payment method now, and we'll automatically process payment on the due date.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Form Modal */}
        {showPaymentForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 sm:p-6 md:p-8 pt-24">
            <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedPaymentOption === 'immediate' ? 'Complete Payment' : 'Set Up Payment Method'}
                </h3>
                <button
                  onClick={() => {
                    setShowPaymentForm(false);
                    setSelectedPaymentOption(null);
                    // If cancelling approval, re-enable button (but check status first)
                    if (selectedPaymentOption === 'due_date') {
                      checkApprovalStatus();
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <Elements 
                stripe={stripePromise} 
                options={{ 
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#1F11ED',
                    }
                  }
                }}
                onLoadError={(error) => {
                  console.error('Elements load error:', error);
                }}
              >
                <PaymentForm />
              </Elements>
            </div>
          </div>
        )}

        {/* Retry Payment Form Modal */}
        {showRetryForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 sm:p-6 md:p-8 pt-24">
            <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Retry Payment</h3>
                <button
                  onClick={() => setShowRetryForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <Elements stripe={stripePromise}>
                <RetryPaymentForm />
              </Elements>
            </div>
          </div>
        )}

        {/* Rejection Form Modal */}
        {showRejectionForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 sm:p-6 md:p-8 pt-24">
            <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Reject Invoice</h3>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Please provide a detailed reason for rejecting this invoice (minimum 10 characters)..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum 10 characters required
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowRejectionForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Reject Invoice'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Success Modal */}
        {showSuccessModal && successDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[99999] p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center shadow-2xl">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {successDetails.type === 'immediate' ? 'Payment Successful!' : 'Payment Method Saved!'}
              </h2>
              
              {successDetails.type === 'immediate' ? (
                <>
                  <p className="text-gray-600 mb-6">
                    Your payment of <span className="font-bold text-green-600">{formatCurrency(successDetails.amount)}</span> has been processed successfully.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Invoice Number:</span>
                      <span className="text-sm font-medium text-gray-900">{successDetails.invoiceNumber}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Amount Paid:</span>
                      <span className="text-sm font-medium text-green-600">{formatCurrency(successDetails.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Status:</span>
                      <span className="text-sm font-medium text-green-600">PAID</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    Your payment method has been securely saved. You will be automatically charged <span className="font-bold text-blue-600">{formatCurrency(successDetails.amount)}</span> on <span className="font-bold">{new Date(successDetails.dueDate).toLocaleDateString('en-GB')}</span>.
                  </p>
                  <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Invoice Number:</span>
                      <span className="text-sm font-medium text-gray-900">{successDetails.invoiceNumber}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600">Scheduled Amount:</span>
                      <span className="text-sm font-medium text-blue-600">{formatCurrency(successDetails.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Payment Date:</span>
                      <span className="text-sm font-medium text-gray-900">{new Date(successDetails.dueDate).toLocaleDateString('en-GB')}</span>
                    </div>
                  </div>
                </>
              )}
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    window.location.reload();
                  }}
                  className="w-full bg-[#1F11ED] text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
                <button
                  onClick={() => window.location.href = 'https://www.receevit.com'}
                  className="w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Visit Receevit®
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#1F11ED] mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                  <span className="text-[#1F11ED] font-bold text-xl">R</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Receevit®</h3>
                  <p className="text-blue-100 text-sm">Professional Invoice Management</p>
                </div>
              </div>
              <p className="text-blue-200 text-sm leading-relaxed mb-4">
                Streamline your invoicing process with our comprehensive SaaS platform. 
                Manage invoices, automate approvals, and get paid faster.
              </p>
              <button
                onClick={() => window.open('https://www.receevit.com', '_blank')}
                className="bg-white text-[#1F11ED] px-4 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
              >
                Visit Homepage
              </button>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Features</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Pricing</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Documentation</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Support</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Help Center</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Contact Us</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Live Chat</a></li>
                <li><a href="#" className="text-blue-200 hover:text-white text-sm transition-colors">Status</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-blue-600 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-blue-200 text-sm">
                © {new Date().getFullYear()} Receevit®. All rights reserved.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-4 md:mt-0">
                <Link to="/privacy" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Terms of Use
                </Link>
                <Link to="/cookies" className="text-blue-200 hover:text-white text-sm transition-colors">
                  Cookie Policy
                </Link>
                <button
                  type="button"
                  onClick={openPreferences}
                  className="text-blue-200 hover:text-white text-sm transition-colors"
                >
                  Cookie Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GuestInvoicePage;
